// @ts-nocheck
import { SettingsCard, SettingsGroup, SettingsRow } from './SettingsPrimitives';
import { useSettingsSavedToast } from './useSettingsSavedToast';
import { ChangePasswordCard } from './ChangePasswordCard';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, Loader2, Upload, Trash2, Check, SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

/**
 * Where « Réglages d'appel » live: the ⚙ popover of the list (/ParallelDialer).
 * ?callSettings=open asks the list to open it on arrival.
 */
export const CALL_SETTINGS_PATH = '/ParallelDialer?callSettings=open';

// ─── Section: Général ─────────────────────────────────────────────────────────

// ─── Voicemail drop ────────────────────────────────────────────────────────
// Le message deposé quand un prospect ne decroche pas. Il avait sa page
// (/vm-drops) ; il est maintenant une carte de la section Général.
function VoicemailDropConfig({ d, uploadingVoicemail, setUploadingVoicemail }) {
  const { t } = useTranslation(['settings', 'common']);
  // Choix UI session (fichier upload vs enregistrement live) — pas de
  // persistance: la valeur effective qui compte est `voicemailAudioUrl`
  // côté serveur, peu importe par quel mode l'utilisateur l'a uploadé.
  const [mode, setMode] = useState('file');

  const [recording, setRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState('');
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const recordingTimerRef = React.useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setRecordedBlob(blob);
        setRecordedUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((track) => track.stop());
      };
      mr.start();
      setRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime((n) => n + 1), 1000);
    } catch (err) {
      toast.error(t('settings:voicemailDrop.micDenied'));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
    }
  };

  const discardRecording = () => {
    setRecordedBlob(null);
    if (recordedUrl) { URL.revokeObjectURL(recordedUrl); setRecordedUrl(''); }
    setRecordingTime(0);
  };

  const uploadRecording = async () => {
    if (!recordedBlob) return;
    setUploadingVoicemail(true);
    try {
      const formData = new FormData();
      formData.append('audio', recordedBlob, 'voicemail-live.webm');
      const resp = await fetch('/api/twilio/voicemail-audio', { method: 'POST', body: formData });
      const result = await resp.json();
      if (result.success) {
        d.setVoicemailAudioUrl(result.audioUrl);
        toast.success(t('settings:voicemailDrop.recordingUploaded'));
        discardRecording();
      } else {
        toast.error(t('settings:voicemailDrop.uploadError'));
      }
    } catch {
      toast.error(t('settings:voicemailDrop.networkError'));
    } finally {
      setUploadingVoicemail(false);
    }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="px-5 py-4 space-y-4">
      {/* Mode selector */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setMode('file')}
          className={`flex-1 px-3 py-2 rounded-md text-xs font-medium border transition-colors ${
            mode === 'file' ? 'bg-[var(--vf-accent-bg)] border-[var(--vf-accent-bd)] text-[var(--vf-accent-text)]' : 'border-border text-muted-foreground hover:bg-muted/50'
          }`}
        >
          {t('settings:voicemailDrop.modeFile')}
        </button>
        <button
          type="button"
          onClick={() => setMode('live')}
          className={`flex-1 px-3 py-2 rounded-md text-xs font-medium border transition-colors ${
            mode === 'live' ? 'bg-[var(--vf-accent-bg)] border-[var(--vf-accent-bd)] text-[var(--vf-accent-text)]' : 'border-border text-muted-foreground hover:bg-muted/50'
          }`}
        >
          {t('settings:voicemailDrop.modeLive')}
        </button>
      </div>

      {/* Mode "file" — upload existant */}
      {mode === 'file' && (
        <div>
          <p className="text-xs text-muted-foreground mb-2">{t('settings:voicemailDrop.fileHint')}</p>
          {d.voicemailAudioUrl ? (
            <div className="flex items-center gap-2">
              <audio src={d.voicemailAudioUrl} controls className="h-8 flex-1" style={{ minWidth: 0 }} />
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                onClick={() => { d.setVoicemailAudioUrl(''); }}
                aria-label={t('settings:voicemailDrop.removeMessage')}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ) : (
            <label className="flex items-center justify-center w-full h-9 text-xs border border-dashed border-border rounded-md cursor-pointer hover:border-[var(--vf-accent-bd)] hover:bg-[var(--vf-accent-bg)] transition-colors">
              <input
                type="file"
                accept="audio/*"
                className="hidden"
                disabled={uploadingVoicemail}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 5 * 1024 * 1024) { toast.error(t('settings:voicemailDrop.fileTooLarge')); return; }
                  setUploadingVoicemail(true);
                  try {
                    const formData = new FormData();
                    formData.append('audio', file);
                    const resp = await fetch('/api/twilio/voicemail-audio', { method: 'POST', body: formData });
                    const result = await resp.json();
                    if (result.success) { d.setVoicemailAudioUrl(result.audioUrl); toast.success(t('settings:voicemailDrop.voicemailUploaded')); }
                    else toast.error(t('settings:voicemailDrop.uploadError'));
                  } catch { toast.error(t('settings:voicemailDrop.networkError')); }
                  finally { setUploadingVoicemail(false); e.target.value = ''; }
                }}
              />
              {uploadingVoicemail
                ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />{t('settings:voicemailDrop.uploading')}</>
                : <><Upload className="w-3 h-3 mr-1.5" />{t('settings:voicemailDrop.uploadFile')}</>}
            </label>
          )}
        </div>
      )}

      {/* Mode "live" — enregistrement micro */}
      {mode === 'live' && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            {t('settings:voicemailDrop.liveHint')}
          </p>

          {/* Audio actuel persisté */}
          {d.voicemailAudioUrl && !recordedUrl && !recording && (
            <div className="space-y-1">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">{t('settings:voicemailDrop.currentRecording')}</p>
              <div className="flex items-center gap-2">
                <audio src={d.voicemailAudioUrl} controls className="h-8 flex-1" style={{ minWidth: 0 }} />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                  onClick={() => { d.setVoicemailAudioUrl(''); }}
                  aria-label={t('settings:voicemailDrop.removeMessage')}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}

          {/* Bouton record / stop */}
          {!recordedBlob && (
            <div className="flex items-center gap-3">
              {!recording ? (
                <Button
                  size="sm"
                  onClick={startRecording}
                  className="h-9 bg-red-500 hover:bg-red-600 text-white"
                >
                  <Mic className="w-4 h-4 mr-2" />
                  {t('settings:voicemailDrop.startRecording')}
                </Button>
              ) : (
                <>
                  <Button
                    size="sm"
                    onClick={stopRecording}
                    variant="outline"
                    className="h-9"
                  >
                    <span className="w-2 h-2 rounded-sm bg-red-500 mr-2" />
                    {t('settings:voicemailDrop.stop')}
                  </Button>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {formatTime(recordingTime)}
                  </span>
                </>
              )}
            </div>
          )}

          {/* Aperçu de l'enregistrement avant upload */}
          {recordedBlob && recordedUrl && (
            <div className="space-y-2">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">{t('settings:voicemailDrop.preview')}</p>
              <audio src={recordedUrl} controls className="h-8 w-full" />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={uploadRecording}
                  disabled={uploadingVoicemail}
                  className="h-8 flex-1"
                >
                  {uploadingVoicemail
                    ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />{t('settings:voicemailDrop.uploading')}</>
                    : <><Check className="w-3 h-3 mr-1.5" />{t('settings:voicemailDrop.useRecording')}</>}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={discardRecording}
                  disabled={uploadingVoicemail}
                  className="h-8"
                >
                  {t('settings:voicemailDrop.restart')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function GeneralSection({ d }) {
  const { t } = useTranslation(['settings', 'common']);
  const navigate = useNavigate();
  const [uploadingVoicemail, setUploadingVoicemail] = useState(false);

  // Toast after the server answers the save of a change made here (RUI-09).
  useSettingsSavedToast(JSON.stringify({
    language: d.language,
    powerModePauseBetweenBatches: d.powerModePauseBetweenBatches,
    powerModeLeaveVoicemail: d.powerModeLeaveVoicemail,
    voicemailAudioUrl: d.voicemailAudioUrl,
  }));

  // W4 (B2-settings-general): three groups, Appels (Power Mode), Audio and
  // Compte. The call settings that the list's ⚙ popover also set (parallel
  // calls, ring time, attempts per contact, sounds, closing callbacks) are
  // set there only: « Réglages d'appel » live where one calls (Minari's
  // Call settings), and this page links to them. What the popover does not
  // carry (the pause between batches, the voicemail message) stays here.
  // The meeting link left too (DUP-1): it belongs to the client, and is set
  // in Organisation > Intégrations by an administrator.
  return (
    <div className="space-y-8">
      <SettingsGroup title={t('settings:general.groupCalls')}>
        <SettingsCard>
          <SettingsRow label={t('settings:general.callSettingsLabel')} hint={t('settings:general.callSettingsHint')}>
            <Button
              variant="outline"
              size="sm"
              className="h-9"
              data-testid="open-call-settings"
              onClick={() => navigate(CALL_SETTINGS_PATH)}
            >
              <SlidersHorizontal className="w-4 h-4 mr-1.5" aria-hidden />
              {t('settings:general.callSettingsOpen')}
            </Button>
          </SettingsRow>

          {/* Power Mode — pause entre batches */}
          <SettingsRow label={t('settings:general.batchPauseLabel')} hint={t('settings:general.batchPauseHint')} last>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={0}
                max={120}
                step={1}
                aria-label={t('settings:general.batchPauseLabel')}
                value={d.powerModePauseBetweenBatches}
                onChange={(e) => {
                  const n = parseInt(e.target.value);
                  d.setPowerModePauseBetweenBatches(Number.isFinite(n) && n >= 0 ? n : 0);
                }}
                className="w-24 h-9"
              />
              <span className="text-sm text-muted-foreground">{t('settings:ai.seconds')}</span>
            </div>
          </SettingsRow>
        </SettingsCard>

        {/* Voicemail drop. Le toggle decide si Twilio depose un message ou
            raccroche ; le message lui-meme n'a de sens que si le toggle est ON,
            d'ou l'affichage conditionnel. */}
        <SettingsCard>
          <SettingsRow
            label={t('settings:general.voicemailDropLabel')}
            hint={t('settings:general.voicemailDropHint')}
            last={!d.powerModeLeaveVoicemail}
          >
            <Switch
              checked={d.powerModeLeaveVoicemail}
              onCheckedChange={d.setPowerModeLeaveVoicemail}
              aria-label={t('settings:general.voicemailDropLabel')}
            />
          </SettingsRow>
          {d.powerModeLeaveVoicemail && (
            <VoicemailDropConfig
              d={d}
              uploadingVoicemail={uploadingVoicemail}
              setUploadingVoicemail={setUploadingVoicemail}
            />
          )}
        </SettingsCard>
      </SettingsGroup>

      {/* The microphone is chosen in the call settings popover (single place, W4); the output device stays here. */}
      <SettingsGroup title={t('settings:general.groupAudio')}>
        <SettingsCard>
          <div className="px-5 py-4">
            <label htmlFor="settings-audio-output" className="block text-sm font-medium text-foreground mb-2">{t('settings:general.audioOutput')}</label>
            <select
              id="settings-audio-output"
              value={d.audioOutputDevice}
              onChange={(e) => d.setAudioOutputDevice(e.target.value)}
              className="w-full h-9 bg-card border border-border text-foreground/80 text-sm rounded-md px-2"
            >
              {d.availableDevices.outputs.map((device, i) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || t('settings:general.speakerFallback', { index: i + 1 })}
                </option>
              ))}
            </select>
          </div>
        </SettingsCard>
      </SettingsGroup>

      <SettingsGroup title={t('settings:general.groupAccount')}>
        <SettingsCard>
          <SettingsRow label={t('common:language.label')} last>
            <Select value={d.language} onValueChange={d.setLanguage}>
              <SelectTrigger className="w-40 h-9" aria-label={t('common:language.label')}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">{t('common:language.fr')}</SelectItem>
                <SelectItem value="en">{t('common:language.en')}</SelectItem>
              </SelectContent>
            </Select>
          </SettingsRow>
        </SettingsCard>
        <ChangePasswordCard />
      </SettingsGroup>
    </div>
  );
}
